import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AuthAppService } from '@modules/auth';
import { map, take } from 'rxjs';

/**
 * SEC-003 : Garde d'autorisation basée sur les rôles.
 * Utilise `data.roles` de la route pour vérifier les droits de l'utilisateur connecté.
 * Doit être appliqué après AuthGuard (l'utilisateur est déjà authentifié à ce stade).
 *
 * Usage dans les routes :
 *   canActivate: [AuthGuard, RoleGuard],
 *   data: { roles: ['Admin'] }
 */
export const RoleGuard: CanActivateFn | CanActivateChildFn = (route: ActivatedRouteSnapshot) => {
    const authService = inject(AuthAppService);
    const router = inject(Router);

    const requiredRoles: string[] = route.data?.['roles'] ?? [];

    // Si aucun rôle n'est requis, on laisse passer
    if (requiredRoles.length === 0) {
        return true;
    }

    return authService.user$.pipe(
        take(1),
        map(user => {
            if (!user) {
                return router.parseUrl('/sign-in');
            }

            const userRole: string = user.role ?? '';
            const userRights: string[] = user.rights ?? [];

            const hasAccess = requiredRoles.some(
                role => userRole === role || userRights.includes(role)
            );

            if (!hasAccess) {
                return router.parseUrl('/home');
            }

            return true;
        })
    );
};
